import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Filter + pagination state stored in the URL query string so a filtered view is
 * shareable and survives refresh. Mirrors the same useSearchParams pattern used
 * by the public-* pages in this app.
 *
 * Shape:
 *   page                 number (default 1)
 *   q                    string
 *   stage                string[]   (multi-select)
 *   status               string[]   (multi-select)
 *   expiring_within_days number|undefined
 *   expired_unactioned   boolean
 *   sort                 'issued_at' | 'expires_at'
 *   order                'asc' | 'desc'
 */
const LIMIT = 25;

// Newest activity first. The backend's own default is expires_at/asc, which
// surfaces links that lapsed months ago and buries anything recent — useless as
// a landing view for a screen people open to see what is happening now.
export const DEFAULT_SORT = 'issued_at';
export const DEFAULT_ORDER = 'desc';

export const useLinkTokenQueryParams = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const filters = useMemo(() => {
    const days = searchParams.get('expiring_within_days');
    return {
      page: parseInt(searchParams.get('page') || '1', 10) || 1,
      limit: LIMIT,
      q: searchParams.get('q') || '',
      stage: searchParams.getAll('stage'),
      status: searchParams.getAll('status'),
      expiring_within_days: days ? parseInt(days, 10) : undefined,
      expired_unactioned: searchParams.get('expired_unactioned') === 'true',
      sort: searchParams.get('sort') || DEFAULT_SORT,
      order: searchParams.get('order') || DEFAULT_ORDER,
    };
  }, [searchParams]);

  // Update a subset of filters. Any change other than `page` resets to page 1.
  // NOTE: react-router-dom 6.0.2's setSearchParams does NOT support a function
  // updater — it passes the argument straight to createSearchParams, so a
  // function yields empty params and clears the query. We therefore build a
  // concrete URLSearchParams from the CURRENT searchParams (closure) and pass
  // that instance.
  const setFilters = useCallback(
    (patch) => {
      const next = new URLSearchParams(searchParams);
      const resetPage = !('page' in patch);

      Object.entries(patch).forEach(([key, value]) => {
        next.delete(key);
        if (value === undefined || value === null || value === '' || value === false) {
          return;
        }
        if (Array.isArray(value)) {
          value.forEach((v) => next.append(key, v));
        } else if (value === true) {
          next.set(key, 'true');
        } else {
          next.set(key, String(value));
        }
      });

      if (resetPage) next.delete('page');
      setSearchParams(next);
    },
    [searchParams, setSearchParams]
  );

  const clearFilters = useCallback(() => {
    setSearchParams(new URLSearchParams());
  }, [setSearchParams]);

  const hasActiveFilters =
    Boolean(filters.q) ||
    filters.stage.length > 0 ||
    filters.status.length > 0 ||
    filters.expiring_within_days !== undefined ||
    filters.expired_unactioned;

  return { filters, setFilters, clearFilters, hasActiveFilters };
};
